// PQ-tuning popout. Reads everything from pqTuningStore (state machine) +
// useLiveFeed (WS-driven inominal_status for terminal transitions).
//
// State lifecycle per plan §"Dialog states":
//   closed → loading → cold-start | ready → confirming → dispatching
//          → syncing → success | failed | invalid
//
// On unmount during 'syncing', the store retains the syncing state. The
// next time the badge is clicked, the dialog re-opens directly into syncing.
//
// See plan: docs/plans/2026-05-15-feat-pq-tuning-dashboard-plan.md

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLiveFeed } from "@/stores/liveFeed";
import { usePqTuningStore } from "@/stores/pqTuningStore";
import { IS_ADMIN_HOST } from "@/lib/auth";

interface Props {
  windowId?: string;
}

export function PqTuningDialog({ windowId }: Props) {
  const dialog = usePqTuningStore((s) => s.dialog);
  const close = usePqTuningStore((s) => s.close);
  const setInput = usePqTuningStore((s) => s.setInput);
  const beginConfirm = usePqTuningStore((s) => s.beginConfirm);
  const cancelConfirm = usePqTuningStore((s) => s.cancelConfirm);
  const submit = usePqTuningStore((s) => s.submitRecalibrate);
  const onStatusFromWs = usePqTuningStore((s) => s.onStatusFromWs);
  const flaggingOptOut = usePqTuningStore((s) => s.flaggingOptOut);
  const toggleFlaggingOptOut = usePqTuningStore((s) => s.toggleFlaggingOptOut);

  // Subscribe to the active system's inominal_status via WS-fed liveFeed
  // and forward terminal transitions to the store. Dialog re-mounts pick
  // this up because the store retains 'syncing'.
  const activeSystemId =
    dialog.kind === "closed" ? null : dialog.systemId;
  const wsService = useLiveFeed((s) =>
    activeSystemId ? s.services[activeSystemId] : undefined,
  );
  const wsStatus = wsService?.inominal_status ?? null;
  const wsAmps = wsService?.inominal_amps ?? null;

  useEffect(() => {
    if (!activeSystemId) return;
    if (wsStatus === null || wsStatus === "syncing") return;
    onStatusFromWs(activeSystemId, wsStatus, wsAmps);
  }, [activeSystemId, wsStatus, wsAmps, onStatusFromWs]);

  // Auto-close 'success' after 3s; everything else stays until user dismisses.
  useEffect(() => {
    if (dialog.kind !== "success") return;
    const id = setTimeout(close, 3000);
    return () => clearTimeout(id);
  }, [dialog.kind, close]);

  const isOpen =
    dialog.kind !== "closed" &&
    // 'syncing' state persists in the store even when the dialog is closed.
    // Don't auto-open the dialog from that state — only from explicit click.
    !(dialog.kind === "syncing" && wsService === undefined);

  if (!isOpen) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent>
        {dialog.kind === "loading" && (
          <>
            <DialogHeader>
              <DialogTitle>Loading…</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-muted-foreground py-4">
              Reading tuning snapshot for {dialog.systemId}…
            </div>
          </>
        )}

        {dialog.kind === "cold-start" && (
          <>
            <DialogHeader>
              <DialogTitle>{dialog.systemId} — PQ tuning</DialogTitle>
              <DialogDescription>
                {dialog.reason === "stalled"
                  ? "Tuning cache is offline. Recalibration is disabled until cache recovery."
                  : "Tuning cache is warming up — try again in <60 seconds."}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground py-2">
              <div>Recommended iNominal: —</div>
              <div>Events captured (last hour): —</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>Close</Button>
            </DialogFooter>
          </>
        )}

        {(dialog.kind === "ready" || dialog.kind === "confirming") &&
          (() => {
            const data = dialog.data;
            const sev = data.events_by_severity ?? {};
            const inputAmps =
              dialog.kind === "ready" ? dialog.inputAmps : dialog.targetAmps;
            const recDiff =
              data.current_inominal_amps != null &&
              inputAmps !== data.current_inominal_amps;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{dialog.systemId} — PQ tuning</DialogTitle>
                  <DialogDescription>
                    Node {data.node_id ?? "—"} ·{" "}
                    {data.flagged
                      ? data.reason === "upward_noise"
                        ? "iNominal too low for current load — noise risk"
                        : "iNominal high for current load — may miss inrush"
                      : "Tuning looks OK"}
                  </DialogDescription>
                </DialogHeader>

                {data.retune_recommended && (
                  <div className="rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-300 text-sm p-2 font-medium">
                    Retune recommended: iNominal{" "}
                    <span className="font-mono">
                      {data.current_inominal_amps ?? "—"} A
                    </span>{" "}
                    →{" "}
                    <span className="font-mono">
                      {data.recommended_inominal_amps ?? "—"} A
                    </span>
                    <div className="text-xs font-normal text-sky-400/80 mt-0.5">
                      Measured load has drifted and held steady. Confirm the
                      reboot below to apply.
                    </div>
                  </div>
                )}

                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="text-muted-foreground">Current iNominal</div>
                    <div className="font-mono">
                      {data.current_inominal_amps ?? "—"} A
                    </div>
                    <div className="text-muted-foreground">Swell threshold</div>
                    <div className="font-mono">
                      {data.swell_threshold_a ?? "—"} A
                      <span className="text-xs text-muted-foreground ml-1">
                        ({data.swell_threshold_pct}%)
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      Measured current (p95, 30 min)
                    </div>
                    <div className="font-mono">
                      {data.p95_current_30m?.toFixed(1) ?? "—"} A
                    </div>
                    <div className="text-muted-foreground">Peak (15 min)</div>
                    <div className="font-mono">
                      {data.peak_current_15m?.toFixed(1) ?? "—"} A
                    </div>
                    <div className="text-muted-foreground">Avg (15 min)</div>
                    <div className="font-mono">
                      {data.avg_current_15m?.toFixed(1) ?? "—"} A
                    </div>
                    <div className="text-muted-foreground">Events (last hour)</div>
                    <div className="font-mono">
                      {data.events_last_hour ?? "—"}
                      {(sev.severe ?? 0) +
                        (sev.moderate ?? 0) +
                        (sev.minor ?? 0) >
                        0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          (severe {sev.severe ?? 0}, moderate {sev.moderate ?? 0}, minor {sev.minor ?? 0})
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground">
                      Events (since event start)
                    </div>
                    <div className="font-mono">
                      {data.events_since_event_start ?? "—"}
                    </div>
                  </div>

                  <div className="border-t border-border pt-3 space-y-2">
                    <Label htmlFor="pq-amps">
                      Recommended iNominal:{" "}
                      <span className="font-mono">
                        {data.recommended_inominal_amps ?? "—"} A
                      </span>
                    </Label>
                    {/* The editable target + recalibrate action are admin-
                        only — recalibration reboots the meter and the API
                        gates it behind CF Access. Non-admin sees the
                        recommendation read-only. */}
                    {IS_ADMIN_HOST && (
                      <Input
                        id="pq-amps"
                        type="number"
                        min={15}
                        max={500}
                        step={5}
                        disabled={dialog.kind === "confirming"}
                        value={inputAmps}
                        onChange={(e) => setInput(Number(e.target.value))}
                      />
                    )}
                    {dialog.kind === "confirming" && (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-500 text-xs p-2">
                        This will reboot the meter (~1 min downtime). No PQ
                        events captured during the reboot window. Cancel below
                        to abort.
                      </div>
                    )}
                  </div>

                  {data.inominal_history.length > 0 && (
                    <div className="border-t border-border pt-3 space-y-1">
                      <div className="text-muted-foreground text-xs uppercase tracking-wide">
                        iNominal change history
                      </div>
                      <ul className="space-y-0.5">
                        {data.inominal_history.slice(0, 5).map((h, idx) => (
                          <li
                            key={idx}
                            className="flex justify-between gap-2 text-xs"
                          >
                            <span className="font-mono">
                              {h.old_amps ?? "—"} A → {h.new_amps} A
                            </span>
                            <span className="text-muted-foreground">
                              {h.set_by}
                              {h.changed_at
                                ? ` · ${new Date(h.changed_at).toLocaleString()}`
                                : ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="border-t border-border pt-3 flex items-center justify-between gap-2">
                    <Label
                      htmlFor="pq-flag-optout"
                      className="text-xs text-muted-foreground font-normal"
                    >
                      Flag this meter for iNominal drift
                    </Label>
                    <input
                      id="pq-flag-optout"
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer accent-sky-500"
                      checked={!flaggingOptOut.has(dialog.systemId)}
                      onChange={() => toggleFlaggingOptOut(dialog.systemId)}
                    />
                  </div>
                </div>

                <DialogFooter>
                  {!IS_ADMIN_HOST ? (
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">
                        Recalibration is admin-only — open the admin dashboard
                        to apply.
                      </span>
                      <Button variant="outline" onClick={close}>
                        Close
                      </Button>
                    </div>
                  ) : dialog.kind === "ready" ? (
                    <>
                      <Button variant="outline" onClick={close}>
                        Close
                      </Button>
                      <Button
                        onClick={() => beginConfirm(dialog.inputAmps)}
                        disabled={!recDiff}
                      >
                        Recalibrate to {inputAmps} A
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={cancelConfirm}>
                        Cancel
                      </Button>
                      <Button onClick={() => submit(windowId)}>
                        Confirm reboot
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </>
            );
          })()}

        {dialog.kind === "dispatching" && (
          <>
            <DialogHeader>
              <DialogTitle>{dialog.systemId}</DialogTitle>
              <DialogDescription>Dispatching recalibration…</DialogDescription>
            </DialogHeader>
            <div className="text-sm text-muted-foreground py-2">
              Sending to meter: iNominal → {dialog.targetAmps} A
            </div>
          </>
        )}

        {dialog.kind === "syncing" && (
          <>
            <DialogHeader>
              <DialogTitle>{dialog.systemId}</DialogTitle>
              <DialogDescription>
                Recalibration in progress — meter offline ~1 minute
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm space-y-2 py-2">
              <div>
                Target iNominal:{" "}
                <span className="font-mono">{dialog.targetAmps} A</span>
              </div>
              <SyncProgressHint dispatchedAt={dialog.dispatchedAt} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Close (sync continues in background)
              </Button>
            </DialogFooter>
          </>
        )}

        {dialog.kind === "success" && (
          <>
            <DialogHeader>
              <DialogTitle>{dialog.systemId} — recalibrated</DialogTitle>
              <DialogDescription>
                iNominal updated to {dialog.finalAmps} A. Closing automatically.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close}>Close</Button>
            </DialogFooter>
          </>
        )}

        {(dialog.kind === "failed" || dialog.kind === "invalid") && (
          <>
            <DialogHeader>
              <DialogTitle>{dialog.systemId} — recalibration failed</DialogTitle>
              <DialogDescription className="text-destructive">
                {dialog.kind === "invalid"
                  ? "Meter rejected this value — likely out of CT-derived range. Try a different value."
                  : `Recalibration failed: ${dialog.reason}`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close}>Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Show "Taking longer than expected" hint after 2 min in syncing state.
function SyncProgressHint({ dispatchedAt }: { dispatchedAt: number }) {
  const elapsed = (Date.now() - dispatchedAt) / 1000;
  if (elapsed < 120) {
    return (
      <div className="text-xs text-muted-foreground">
        Expected: ~60–90 seconds. Status will update automatically.
      </div>
    );
  }
  return (
    <div className="text-xs text-amber-500">
      Taking longer than expected ({Math.round(elapsed)}s elapsed). If this
      continues, the cache will reconcile to 'failed' after 10 minutes; you
      can then retry.
    </div>
  );
}
